import { useEffect, useMemo, useRef } from 'react'
import useFilterYears from '../../hooks/useFilterYears'
import useGlobalState from '../../hooks/useGlobalState'
import useTraverseDataSet from '../../hooks/useTraverseDataSet'
import {
    NyuData,
    NyuDataPointsWithParsedTime,
    NyuGraphData,
    NyuSeriesProperties,
} from '../../../../shared/types'
import {
    customOptions,
    getGroupsFromSeriesProperties,
    isCustomOption,
    parseTimeValue,
    selectizeLabelToPathSegment,
} from '../../utils/helpers'
import CustomChart from '../CustomChart'
import Dropdown from '../Dropdown'
import { LoadingIndicator } from '../LoadingIndicator'
import TabButtons from '../TabButtons'
import { cn } from '../../utils/cn'
import DownloadDataButton from '../DownloadDataButton'
import HeaderLogos from '../HeaderLogos'
import { canActivateHighContrastMode } from '../../hooks/useHighContrastMode'
import HighContrastToggle from '../Graph/HighContrastMode/HighContrastToggle'

export default function PageSelectize({ data }: { data: NyuData }) {
    const result = useTraverseDataSet(data.dataset)

    const lastValidResult = useRef<ReturnType<
        typeof useTraverseDataSet
    > | null>(null)
    const graphDataValid =
        result.graphData && typeof result.graphData === 'object'
    useEffect(() => {
        if (graphDataValid) {
            lastValidResult.current = result
        }
    }, [graphDataValid, result])

    const usedResult = graphDataValid ? result : lastValidResult.current
    const usedDataValid = typeof usedResult?.graphData === 'object'

    if (!usedDataValid) {
        return <LoadingIndicator />
    }

    const { graphData, path, pathSegmentsOptions, setPathSegment } = usedResult
    return (
        <PageSelectizeWithData
            data={data}
            graphData={graphData!}
            path={path!}
            pathSegmentsOptions={pathSegmentsOptions!}
            setPathSegment={setPathSegment!}
        />
    )
}

function PageSelectizeWithData({
    data,
    graphData,
    path,
    pathSegmentsOptions,
    setPathSegment,
}: {
    data: NyuData
    graphData: NyuGraphData
    path: string[]
    pathSegmentsOptions: string[][]
    setPathSegment: (index: number, value: string) => void
}) {
    const downloadView = useGlobalState((state) => state.downloadView)

    const {
        meta: { title },
        uiProperties,
    } = data

    const { seriesProperties, dataProperties, selectizeProperties } = graphData!

    const groups = useMemo(() => {
        return getGroupsFromSeriesProperties(
            seriesProperties as NyuSeriesProperties
        )
    }, [seriesProperties])

    const activeDataPointKeys = groups.flatMap(({ lines }) =>
        lines.map(({ key }) => key)
    )

    const dataPoints = graphData!.data
    const dataPointsWithParsedTime: NyuDataPointsWithParsedTime = useMemo(
        () => dataPoints.map(parseTimeValue),
        [dataPoints]
    )

    const {
        dataPoints: dataPointsWithFilteredYears,
        startYearDropdownProps,
        endYearDropdownProps,
    } = useFilterYears(dataPointsWithParsedTime, dataProperties)

    const dataPointsHaveDistinctYears = useMemo(() => {
        const allYears = dataPointsWithFilteredYears.map(({ year }) => year)
        const distinctYears = [...new Set(allYears)]
        return distinctYears.length > 1
    }, [dataPointsWithFilteredYears])

    const nextDataPoints = dataPointsHaveDistinctYears
        ? dataPointsWithFilteredYears
        : dataPointsWithParsedTime

    const pathWithoutCustomOption = path
        .filter((option) => {
            const isCustom = customOptions.includes(option)
            return !isCustom
        })
        .map((option) => {
            const [, , optionText] = isCustomOption(option)
            return optionText
        })
    const titleWithSelection = `${title} (${pathWithoutCustomOption.join(' - ')})`
    const prependRows = [[title], [pathWithoutCustomOption.join(', ')], []]

    const hideAll = dataProperties.hideControls?.includes('all')
    const hideSelector =
        dataProperties.hideControls?.includes('selector') || hideAll
    const hideFilter =
        dataProperties.hideControls?.includes('filter') || hideAll

    const trailingOptions = selectizeProperties?.options.slice(-2)
    const pathIncludesYears =
        trailingOptions?.length === 2 &&
        trailingOptions[0] === 'Start Year' &&
        trailingOptions[1] === 'End Year'
    const yearOptionsValid =
        startYearDropdownProps.options.length > 1 ||
        endYearDropdownProps.options.length > 1
    const showYearFilter = !hideFilter && !pathIncludesYears && yearOptionsValid

    const hideDownloadButton =
        downloadView ||
        dataProperties.hideControls?.includes('download') ||
        hideAll

    const hideHighContrastToggle =
        downloadView ||
        !canActivateHighContrastMode(dataProperties, seriesProperties) ||
        dataProperties.hideControls?.includes('highContrast') ||
        hideAll

    return (
        <div data-testid="chart-section">
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                <div className="text-xl font-bold" data-testid="headline">
                    {title}
                </div>
                {downloadView && (
                    <div className="text-right font-bold">
                        DHL Globalization Tracker
                    </div>
                )}
            </div>
            {!downloadView && uiProperties?.firstSelectorAsTabs && (
                <div className="mb-2">
                    <TabButtons
                        value={path[0]}
                        options={pathSegmentsOptions[0]}
                        testId="dataset-selector-0-dropdown"
                        setValue={(value: string) => setPathSegment(0, value)}
                    />
                </div>
            )}
            <div className="mb-2 flex flex-col flex-wrap items-start justify-between gap-2 md:my-2 md:flex-row md:items-center md:justify-between">
                {downloadView && (
                    <div className="mt-1 inline-flex flex-wrap gap-1 whitespace-nowrap md:mt-0">
                        {path.map((segment: string, index: number) => {
                            const [isCustom, , optionText] =
                                isCustomOption(segment)
                            if (isCustom) {
                                return null
                            }
                            return (
                                <div
                                    key={`${index}_${segment}_download`}
                                    className="group"
                                >
                                    <span className="capitalize text-gray-500">
                                        {selectizeLabelToPathSegment(
                                            selectizeProperties?.options[index]
                                        )}
                                    </span>
                                    <span className="text-gray-500">
                                        {optionText}
                                    </span>
                                    <span className="text-gray-500 group-last:hidden">
                                        ,
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                )}
                {!downloadView && !hideSelector && (
                    <div className="flex w-full grow flex-wrap items-center justify-end gap-2 sm:flex-row md:w-auto">
                        {path.map((segment: string, index: number) => {
                            const hideFirstSelectorAsTabs =
                                index === 0 && uiProperties?.firstSelectorAsTabs

                            const hideSingleEmptyDropdown =
                                index === 0 &&
                                pathSegmentsOptions.length === 1 &&
                                pathSegmentsOptions[0].length <= 1

                            if (
                                hideFirstSelectorAsTabs ||
                                hideSingleEmptyDropdown
                            ) {
                                return null
                            }

                            const label = selectizeProperties?.options[index]
                            const isYear =
                                pathIncludesYears &&
                                (label === 'Start Year' || label === 'End Year')

                            return (
                                <div
                                    key={`${index}_${segment}`}
                                    className={cn(
                                        'flex',
                                        isYear ? 'w-20' : 'flex-1 min-w-28'
                                    )}
                                >
                                    <Dropdown
                                        testId={`dataset-selector-${index}-dropdown`}
                                        className="text-left"
                                        isCombobox={!isYear}
                                        label={label}
                                        value={segment}
                                        sorted={isYear}
                                        options={pathSegmentsOptions[index]}
                                        setValue={(value: string) =>
                                            setPathSegment(index, value)
                                        }
                                    />
                                </div>
                            )
                        })}

                        {showYearFilter && (
                            <>
                                <Dropdown
                                    label="Start Year"
                                    className="ml-auto w-20 text-left"
                                    testId="start-year-dropdown"
                                    {...startYearDropdownProps}
                                />
                                <Dropdown
                                    label="End Year"
                                    className="w-20 text-left"
                                    testId="end-year-dropdown"
                                    {...endYearDropdownProps}
                                />
                            </>
                        )}

                        {!hideHighContrastToggle && <HighContrastToggle />}

                        {!hideDownloadButton && (
                            <DownloadDataButton
                                title={titleWithSelection}
                                dataPoints={nextDataPoints}
                                dataProperties={dataProperties}
                                seriesProperties={seriesProperties}
                                activeDataPointKeys={activeDataPointKeys}
                                pullRight={
                                    !showYearFilter && hideHighContrastToggle
                                }
                                prependRows={prependRows}
                                cite={data.meta.cite}
                            />
                        )}
                    </div>
                )}
                <HeaderLogos />
            </div>
            <CustomChart
                dataPoints={nextDataPoints}
                dataProperties={dataProperties}
                seriesProperties={seriesProperties}
                uiProperties={uiProperties}
                groups={groups}
            />
        </div>
    )
}
